import React, { useEffect, useState } from "react";
import { MessageSquare, Loader2, ArrowRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { onWorkspaceChange } from "@/lib/active-workspace";

export default function History() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/interview/mock/sessions");
      setItems(data);
    } catch { /* transient, swallow */ } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const off = onWorkspaceChange(() => { load(); });
    return () => { off(); };
  }, []);

  return (
    <div data-testid="history-page" className="animate-in max-w-3xl">
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.24em] text-amber-400 mb-2">Progress</div>
        <h1 className="text-3xl font-bold tracking-tight text-zinc-50">Your practice progress</h1>
        <p className="text-sm text-zinc-500 mt-2">
          Every interview you&apos;ve completed and the readiness score you earned. Watch your trend line rise.
        </p>
      </div>

      {loading && (
        <div className="text-sm text-zinc-500 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="p-8 rounded-xl border border-zinc-800/70 bg-zinc-900/40 text-center">
          <MessageSquare className="h-8 w-8 text-zinc-600 mx-auto mb-3" />
          <div className="text-sm text-zinc-300">No mock interviews yet.</div>
          <div className="text-xs text-zinc-500 mt-1">
            Run your first mock interview to start tracking progress.
          </div>
          <Link
            to="/app/practice?step=video"
            data-testid="history-empty-cta"
            className="mt-5 inline-flex items-center gap-2 text-sm bg-zinc-50 text-zinc-950 hover:bg-white font-semibold rounded-lg px-4 py-2"
          >
            Start Practice Interview <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="space-y-2">
          {items.map((m) => (
            <button
              key={m.session_id}
              onClick={() => navigate(`/app/practice?step=video`)}
              data-testid={`history-item-${m.session_id}`}
              className="w-full text-left p-4 rounded-lg border border-zinc-800 bg-zinc-900/40 flex items-center justify-between hover:border-zinc-700 transition-colors"
            >
              <div className="flex items-center gap-3 min-w-0">
                <MessageSquare className="h-4 w-4 text-zinc-500 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm text-zinc-100 truncate">
                    {m.job_title || "Mock session"}
                  </div>
                  <div className="text-xs text-zinc-500">
                    {new Date(m.created_at).toLocaleString()} · {m.status}
                  </div>
                </div>
              </div>
              {m.results?.interview_score !== undefined && (
                <span className="text-xs font-semibold text-amber-300 bg-amber-500/10 border border-amber-500/25 rounded-md px-2 py-1 flex-shrink-0">
                  {m.results.interview_score}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
